import React, { Component, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import {
  ModalFooterControls,
  ModalHeaderControls,
} from "shared/helpers/modal-controls";
import { validateEmail } from "shared/helpers/validate";
import Moderator from "containers/scheduler/timeslot-management-modal/moderator";
import EditSessionView from "containers/scheduler/timeslot-management-modal/edit-session";
import { Dialog, Tooltip, Spinner } from "@blueprintjs/core";
import InvalidateInterviewModal from "containers/interviews/invalidate-interview-modal";
import {
  pathOr,
  path,
  pipe,
  map,
  filter,
  prop,
  differenceWith,
  eqProps,
  contains,
  difference,
  without,
  equals,
  any,
  where,
  trim,
  is,
} from "ramda";
import { parseTimeZone, getUserTimeZone } from "assets/localization";
import {
  capitalizeFirstLetter,
  lessThan24Hours,
} from "shared/helpers/functional";
import { sendSingle } from "store/editSession/editSessionActions";
import { timezoneAbbreviationLabel, isPMOrAdmin } from "shared/helpers/user";

import "less/scheduler/timeslot-management-modal.less";

const handleChange = (props) => (event) => {
  props.onSelect(props.option, event);
};



const ModeratorOption = (props) => (
  <Tooltip
    boundary="window"
    content="This moderator already has an interview scheduled that conflicts with this time. Proceed with caution."
    isDisabled={!props.option.hasConflict}
  >
    <div className="moderator-option" onClick={handleChange(props)}>
      <i className="im-user2" />
      <span className="moderator-option-label">
        <b>{props.option.label}</b> Moderator #{props.option.moderatorId}
      </span>
      {props.option.hasConflict && (
        <div className="interview-conflict-warning">
          <i className="im-warning" />
          <span className="warning-text">INTERVIEW CONFLICT</span>
        </div>
      )}
    </div>
  </Tooltip>
);

class TimeSlotManagementModal extends Component {
  state = {
    showDropDown: false,
    observerEmails: this.props.observerEmails,
    observerEmailsValid: true,
    chosenAction: this.props.chosenAction || "",
    message: "",
    showInvalidateModal: false
  };
  isPMOrAdmin = isPMOrAdmin();

  componentDidMount() {
    this.props.closePopover();
  }

  componentDidUpdate(prevProps) {
    if (
      equals(this.state.observerEmails, prevProps.observerEmails) &&
      !equals(this.props.observerEmails, prevProps.observerEmails)
    ) {
      this.setState({
        observerEmails: [
          ...this.state.observerEmails,
          ...this.props.observerEmails,
        ],
      });
    }
  }

  toggleModal = () => {
    if (this.props.onClose && this.props.isOpen) this.props.onClose();
    this.props.toggleModal();
  };

  /**
   * Get moderators to display, given by:
   * (moderators currently saved to API
   *  + any unsaved additions
   *  - any unsaved deletions)
   */
  getCurrentModerators = () => {
    const pendingModsToAdd = map(
      (mId) => path([mId, "value"], this.props.possibleModeratorsById),
      this.props.pendingModeratorIdToAdd,
    );
    const currentModsPlusAdds = map(
      (mod) =>
        path([mod.moderatorId, "value"], this.props.possibleModeratorsById),
      this.props.moderators,
    ).concat(pendingModsToAdd);
    const moderatorsToDisplay = filter(
      (mod) =>
        Boolean(mod) &&
        !contains(
          prop("moderatorId", mod),
          this.props.pendingModeratorIdsToDelete,
        ),
      currentModsPlusAdds,
    );

    return moderatorsToDisplay;
  };

  chooseAction = (chosenAction) => this.setState({ chosenAction });



  toggleDropDown = () =>
    this.setState({ showDropDown: !this.state.showDropDown });

  sendSingle = (force = false, isInvalidateEmailSent = false) => {
    // Route all flows through saga so it selects the right endpoint per context
    const invalidateRescheduleFlag = Boolean(
      this.props.invalidateReschedule || this.props.invalidateThenReschedule,
    );
    this.props.sendSingle({
      force,
      projectId: this.props.timeSlot.projectId,
      timeSlotId: this.props.timeSlot.timeSlotId,
      action: this.state.chosenAction,
      invalidateReschedule: invalidateRescheduleFlag,
      invalidateThenReschedule: Boolean(this.props.invalidateThenReschedule),
      invalidateSelected:
        this.props.invalidateSelected ||
        this.props.invalidateSelectedFromCompleted,
      isInvalidateEmailSent: this.props.hideBackButton ? false : isInvalidateEmailSent,
      singleModerator: this.props.singleModerator,
      hideBackButton: this.props.hideBackButton,
      isModeratorManagement:
        window.location.href.indexOf("moderatorManagement") > -1,
    });
    // Close immediately, page-level spinner will show
    this.toggleModal();
  };

  handleObserverEmailInput = (event) => {
    const observerEmails = event.target.value
      ? map(trim, event.target.value.split(","))
      : [];
    this.setState({ observerEmails });
  };

  cleanAndValidateEmails = () => {
    const observerEmails = this.state.observerEmails.filter(Boolean);
    const observerEmailsValid = observerEmails.every(validateEmail);

    this.setState({ observerEmails, observerEmailsValid });

    return observerEmailsValid;
  };

  render() {
    // Always render current moderators; avoid hiding list during background fetch
    const currentModerators = this.getCurrentModerators();
    const possibleModerators = Object.values(
      this.props.possibleModeratorsById,
    ).map((possibleMod) => ({
      label: possibleMod.label,
      ...possibleMod.value,
    }));
    const moderatorSelectOptions = differenceWith(
      eqProps("moderatorId"),
      possibleModerators,
      currentModerators,
    );
    const otherModeratorAvailableToHost = any(
      where({ hasConflict: equals(false), isHost: equals(false) }),
      currentModerators,
    );

    const isShortNotice = pipe(prop("startTime"), lessThan24Hours);
    return (
      <Dialog
        className="timeslot-management-modal"
        isOpen={this.props.isOpen}
        onClose={this.toggleModal}
      >
        <ModalHeaderControls
          toggleModal={this.toggleModal}
          innerComponent={
            <h4 className="bp3-dialog-header-title">
              {this.state.chosenAction
                ? `${capitalizeFirstLetter(this.state.chosenAction)} Interview`
                : "Manage Date & Time"}
            </h4>
          }
          firstStep={""}
          currentStep={this.state.chosenAction}
          back={() => {
            this.setState({ showInvalidateModal: true });
            this.props.onBack();
          }}
          modalHeader
          hideBackButton={this.props.isInvalidateReschedule ? false : true}
          showInvalidateModal={this.state.showInvalidateModal}
          fromInvalid={this.props.isInvalidateReschedule}
        />
        {this.state.chosenAction ? (
          <EditSessionView
            chosenAction={this.state.chosenAction}
            interviewDateTime={`${this.props.formatDate(
              this.props.timeSlot.startTime,
              "dddd, MMMM DD, YYYY",
            )} @ ${this.props.formatDate(
              this.props.timeSlot.startTime,
              "h:mm A",
            )} ${parseTimeZone(this.props.timeZone)}`}
            shortNotice={isShortNotice(this.props.timeSlot)}
            {...this.props}
          />
        ) : !this.props.timeSlot ? (
          <Spinner intent="primary" />
        ) : (
          <div className="bp3-dialog-body">
            <div className="interviewee-details">
              <span className="project-name">
                {pathOr("Interview", ["projectName"], this.props.timeSlot)}
              </span>
              <span className="participant-id">
                {(() => {
                  const pid =
                    (this.props.timeSlot &&
                      this.props.timeSlot.intervieweeId) ||
                    (this.props.timeSlot &&
                      this.props.timeSlot.participantId) ||
                    (this.props.timeSlot && this.props.timeSlot.intervieweeID);
                  return `PARTICIPANT ID: #${pid ?? ""}`;
                })()}
              </span>
            </div>
            <div
              className={`time-slot-details ${
                this.isPMOrAdmin ? "reduced" : ""
              }`}
            >
              <h4>Timeslot</h4>
              <div className="interview-date-time">
                <span className="interview-date">{`${this.props.formatDate(
                  this.props.timeSlot.startTime,
                  "ddd MMM. DD, YYYY",
                )}`}</span>
                <span className="interview-time">{`${this.props.formatDate(
                  this.props.timeSlot.startTime,
                  "h:mm A",
                )} - ${this.props.formatDate(
                  this.props.timeSlot.endTime,
                  "h:mm A",
                )} ${timezoneAbbreviationLabel(this.props.timeZone)}`}</span>
              </div>
              <div>
                <div
                  className="reschedule-interview"
                  onClick={() =>
                    this.setState({
                      chosenAction: "reschedule",
                    })
                  }
                >
                  <i className="im-ss-counterclockwise" />
                  <span>
                    <b>Reschedule:</b> Email Participant to request a new date
                    and time.
                  </span>
                </div>
                <div
                  className="cancel-interview"
                  onClick={() => this.chooseAction("cancel")}
                >
                  <i className="im-ss-delete" />
                  <span>
                    <b>Cancel:</b> Email Participant and Moderator to cancel
                    this email.
                  </span>
                </div>
              </div>
            </div>
            <div className="moderator-details">
              <h4>Moderators </h4>
              {currentModerators.map((moderator) => {
                const modEntry =
                  this.props.possibleModeratorsById[moderator.moderatorId];
                if (!modEntry) return null;
                return (
                  <Moderator
                    key={moderator.moderatorId}
                    isCurrentUser={
                      this.props.user.userId === moderator.moderatorId
                    }
                    moderator={modEntry}
                    deleteModeratorFromConference={
                      this.props.deleteModeratorFromConference
                    }
                    canRemoveModerator={
                      !moderator.isHost || otherModeratorAvailableToHost
                    }
                    role={this.props.user.userRole}
                  />
                );
              })}
              {this.state.showDropDown ? (
                <div className="moderator-select">
                  <Select
                    options={moderatorSelectOptions}
                    placeholder="Select Name"
                    onChange={pipe(
                      this.props.addModeratorToConference,
                      this.toggleDropDown,
                    )}
                    optionComponent={ModeratorOption}
                    searchable={false}
                  />
                  <i className="im-ss-delete" onClick={this.toggleDropDown} />
                </div>
              ) : (
                this.isPMOrAdmin && (
                  <button
                    className="btn btn-primary btn-lg btn-block"
                    onClick={this.toggleDropDown}
                  >
                    <i className="im-ss-plus" />
                    Add Moderator
                  </button>
                )
              )}
            </div>
          </div>
        )}
        {this.state.showInvalidateModal && (
          <InvalidateInterviewModal
            isOpen={this.state.showInvalidateModal}
            origin={"moderatorManagement"}
            onClose={() => this.setState({ showInvalidateModal: false })}
            isAlreadyRescheduled={Boolean(
              this.props.timeSlot.isInvalidateEmailSent,
            )}
            onConfirm={({ action, reason, otherReason }) => {
              if (action === "invalidate_and_reschedule") {
                // Return to reschedule view with preserved selection
                this.setState({
                  showInvalidateModal: false,
                  chosenAction: "reschedule",
                });
              } else if (typeof this.props.invalidateInterview === "function") {
                this.props.invalidateInterview({
                  timeSlotId: this.props.timeSlot.timeSlotId,
                  participantId: this.props.timeSlot.intervieweeId,
                  projectId: this.props.timeSlot.projectId,
                  projectName: this.props.timeSlot.projectName,
                  externalSurveyId: this.props.timeSlot.externalSurveyId,
                  origin: "moderatorManagement",
                  action,
                  reason,
                  otherReason,
                  isInvalidateEmailSent: false,
                  onSave: () => this.setState({ showInvalidateModal: false }),
                });
              } else {
                this.setState({ showInvalidateModal: false });
              }
            }}
            projectName={`${pathOr("Interview", ["projectName"], this.props.timeSlot)}${
              this.props.timeSlot.externalSurveyId
                ? ` #${this.props.timeSlot.externalSurveyId}`
                : ""
            }`}
            participantId={this.props.timeSlot.intervieweeId}
            startTime={this.props.timeSlot.startTime}
            endTime={this.props.timeSlot.endTime}
            timeZone={this.props.timeZone}
            formatDate={this.props.formatDate}
            moderatorName={
              pathOr("", ["moderatorLabel"], this.props.timeSlot) ||
              pathOr("", ["singleModerator", "label"], this.props)
            }
            initialReschedule={true}
            initialReason={
              this.props.invalidateSelectedFromCompleted &&
              this.props.invalidateSelectedFromCompleted.reason
                ? this.props.invalidateSelectedFromCompleted.reason
                : ""
            }
            initialOtherReason={
              this.props.invalidateSelectedFromCompleted &&
              this.props.invalidateSelectedFromCompleted.otherReason
                ? this.props.invalidateSelectedFromCompleted.otherReason
                : ""
            }
          />
        )}
        <div className="bp3-dialog-footer">
          <ModalFooterControls
            finalStep
            finalStepLabel={
              this.state.chosenAction === "cancel"
                ? "Cancel Interview & Send Email"
                : this.state.chosenAction === "reschedule"
                  ? this.props.isInvalidateReschedule
                    ? "Confirm & Send"
                    : "Send Email"
                  : "Save"
            }
            finalStepFn={() => {
              if (this.state.chosenAction) {
                this.sendSingle(null, true);
              } else {
                this.toggleModal();
              }
              this.props.onInvalidateRescheduleRevert && this.props.onInvalidateRescheduleRevert();
            }}
            finalStepFnDisabled={
              !this.state.observerEmailsValid || this.props.isSending
            }
            toggleModal={this.toggleModal}
            back={this.props.onBack ? this.props.onBack : this.props.back}
            hideBackButton={this.props.hideBackButton}
          />
        </div>
      </Dialog>
    );
  }
}

const mapStateToProps = (state) => ({
  timeZone: getUserTimeZone(state),
  moderators: state.scheduler.moderatorTimeSlots,
  observerEmails: state.scheduler.observerEmails,
  possibleModeratorsById: state.scheduler.possibleModeratorsById,
  pendingModeratorIdToAdd: state.scheduler.pendingModeratorIdToAdd,
  pendingModeratorIdsToDelete: state.scheduler.pendingModeratorIdsToDelete,
  isLoading: state.scheduler.isLoading,
  singleModerator: state.scheduler.singleModerator,
  user: state.user,
  isSending: state.editSession.isSending
});

const mapDispatch = {
  toggleModal: () => ({ type: "TOGGLE_TIMESLOT_MODAL" }),
  addModeratorToConference: (payload) => ({
    type: "POST_MODERATOR_FOR_CONFERENCE_REQUESTED",
    payload,
  }),
  deleteModeratorFromConference: (payload) => ({
    type: "DELETE_MODERATOR_FROM_CONFERENCE_REQUESTED",
    payload,
  }),
  putUpdatedTimeslotInfo: (payload) => ({
    type: "PUT_UPDATED_TIMESLOT_INFO_REQUESTED",
    payload,
  }),
  sendSingle,
  closePopover: () => ({ type: "CLOSE_TIMESLOT_POPOVER" }),
  invalidateInterview: (payload) => ({
    type: "INVALIDATE_INTERVIEW_REQUESTED",
    payload,
  }),
};

export default connect(mapStateToProps, mapDispatch)(TimeSlotManagementModal);